import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ChildContext } from '../components/Layout/ParentLayout';
import {
  ClipboardDocumentListIcon, ChevronLeftIcon, CheckCircleIcon, ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';

const fmtD  = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'}).replace(/ /g,'-') : '—';
const fmtDT = d => d ? new Date(d).toLocaleString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

const STATUS_COLORS = {
  active:       { bg:'#ecfdf5', color:'#10c98e' },
  under_review: { bg:'#fff7ed', color:'#f97316' },
  completed:    { bg:'#eef2ff', color:'#3b6ef8' },
};
const RATING_COLORS = {
  excellent:        { bg:'#ecfdf5', color:'#10c98e' },
  good:             { bg:'#eef2ff', color:'#3b6ef8' },
  satisfactory:     { bg:'#f3f0ff', color:'#8b5cf6' },
  needs_improvement:{ bg:'#fff7ed', color:'#f97316' },
  concern:          { bg:'#fef2f2', color:'#ef4444' },
};
const GOAL_STATUS_COLORS = {
  not_started:  { bg:'#f0f2f7', color:'#6b7194' },
  in_progress:  { bg:'#fff7ed', color:'#f97316' },
  achieved:     { bg:'#ecfdf5', color:'#10c98e' },
  discontinued: { bg:'#fef2f2', color:'#ef4444' },
};

const css = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .pi*{box-sizing:border-box;} .pi{font-family:'Sora',sans-serif;color:#1a1d2e;animation:piFade .4s ease both;}
  @keyframes piFade{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}
  .pi-title{font-size:1.4rem;font-weight:700;letter-spacing:-.03em;color:#0f1123;margin-bottom:.3rem;}
  .pi-sub{font-size:.78rem;color:#9094a8;margin-bottom:1.5rem;}
  .btn{padding:8px 16px;border-radius:10px;font-size:.8rem;font-weight:600;font-family:'Sora',sans-serif;cursor:pointer;border:none;display:inline-flex;align-items:center;gap:7px;transition:.15s;}
  .btn svg{width:15px;height:15px;stroke-width:2;flex-shrink:0;}
  .btn-orange{background:#f97316;color:white;} .btn-orange:hover{box-shadow:0 4px 14px rgba(249,115,22,.35);}
  .btn-ghost{background:#f0f2f7;color:#6b7194;border:1px solid #e0e4f0;} .btn-ghost:hover{background:#e8ebf4;}
  .btn-sm{padding:5px 12px;font-size:.74rem;} .btn:disabled{opacity:.5;cursor:not-allowed;}
  .card{background:white;border-radius:14px;border:1px solid #e8ebf4;padding:16px 18px;margin-bottom:12px;cursor:pointer;transition:.15s;}
  .card:hover{border-color:#f97316;box-shadow:0 2px 12px rgba(249,115,22,.08);}
  .pill{display:inline-flex;font-size:.67rem;font-weight:700;padding:3px 10px;border-radius:99px;}
  .progress-bar-wrap{height:5px;background:#f0f2f7;border-radius:99px;overflow:hidden;margin-top:8px;}
  .progress-bar{height:100%;background:linear-gradient(90deg,#f97316,#ef4444);border-radius:99px;}
  .empty{text-align:center;padding:52px;color:#b0b5cc;font-size:.82rem;}
  .spin{width:16px;height:16px;border:2px solid #f0e0d0;border-top-color:#f97316;border-radius:50%;animation:sp .7s linear infinite;display:inline-block;}
  @keyframes sp{to{transform:rotate(360deg)}}
  .detail{background:white;border-radius:16px;border:1px solid #e8ebf4;overflow:hidden;}
  .detail-tabs{display:flex;gap:4px;padding:12px 16px;background:#f8f9fc;border-bottom:1px solid #f0f2f8;overflow-x:auto;}
  .dtab{padding:7px 16px;border-radius:9px;border:none;font-family:'Sora',sans-serif;font-size:.78rem;font-weight:600;cursor:pointer;background:transparent;color:#9094a8;white-space:nowrap;transition:.15s;}
  .dtab.on{background:white;color:#0f1123;box-shadow:0 1px 4px rgba(0,0,0,.1);}
  .detail-body{padding:22px;}
  .section-title{font-size:.72rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.07em;margin-bottom:12px;}
  .info-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:16px;}
  .info-item{display:flex;flex-direction:column;gap:4px;}
  .info-lbl{font-size:.67rem;font-weight:700;color:#9094a8;text-transform:uppercase;letter-spacing:.06em;}
  .info-val{font-size:.82rem;color:#1a1d2e;font-weight:500;}
  .goal-card{background:#f8f9fc;border-radius:12px;border:1px solid #e8ebf4;padding:14px;margin-bottom:10px;}
  .update-card{border-left:3px solid #f97316;padding:12px 16px;margin-bottom:12px;background:#fff9f5;border-radius:0 10px 10px 0;}
  .comment-card{background:#f8f9fc;border-radius:12px;padding:12px 14px;margin-bottom:10px;border:1px solid #e8ebf4;}
  .my-comment{background:#fff7ed;border:1px solid #fed7aa;border-radius:12px;padding:12px 14px;margin-bottom:10px;}
  .reply-box{background:#fff7ed;border-radius:8px;padding:8px 12px;margin-top:8px;font-size:.76rem;color:#f97316;}
  .comment-input-wrap{background:#f8f9fc;border-radius:12px;border:1px solid #e8ebf4;padding:14px;margin-top:16px;}
  .comment-input{font-family:'Sora',sans-serif;font-size:.82rem;width:100%;padding:9px 12px;border:1.5px solid #e0e4f0;border-radius:10px;background:white;outline:none;resize:vertical;min-height:80px;}
  .comment-input:focus{border-color:#f97316;}
  .ack-banner{background:#ecfdf5;border:1px solid #a7f3d0;border-radius:12px;padding:12px 16px;display:flex;align-items:center;gap:10px;margin-bottom:16px;font-size:.8rem;color:#065f46;}
  .ack-banner svg{width:20px;height:20px;color:#10c98e;flex-shrink:0;stroke-width:2;}
  .ack-btn-wrap{background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:14px 16px;margin-bottom:16px;}
  @media(max-width:768px){.pi-title{font-size:1.2rem;}.info-grid{grid-template-columns:1fr;}}
`;

export default function ParentILP() {
  const { activeChild } = useContext(ChildContext);
  const [view,       setView]       = useState('list');
  const [ilps,       setIlps]       = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [detailTab,  setDetailTab]  = useState('overview');
  const [comment,    setComment]    = useState('');
  const [sending,    setSending]    = useState(false);

  useEffect(() => {
    if (activeChild?._id) loadIlps();
    else { setIlps([]); setView('list'); setSelected(null); }
  }, [activeChild]);

  const loadIlps = async () => {
    if (!activeChild?._id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/parent/ilp/${activeChild._id}`);
      setIlps(data||[]);
    } catch { toast.error('Failed to load ILPs'); }
    finally { setLoading(false); }
  };

  const openDetail = (ilp) => { setSelected(ilp); setView('detail'); setDetailTab('overview'); };

  const acknowledge = async () => {
    try {
      await api.post(`/parent/ilp/${selected._id}/acknowledge`);
      toast.success('ILP acknowledged');
      const updated = { ...selected, parentAcknowledged: true, parentAcknowledgedAt: new Date().toISOString() };
      setSelected(updated);
      setIlps(p => p.map(i => i._id===selected._id ? updated : i));
    } catch { toast.error('Failed'); }
  };

  const submitComment = async () => {
    if (!comment.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/parent/ilp/${selected._id}/comment`, { comment });
      const updated = { ...selected, parentComments: [...(selected.parentComments||[]), data] };
      setSelected(updated);
      setComment('');
      toast.success('Comment sent');
    } catch { toast.error('Failed'); }
    finally { setSending(false); }
  };

  const goalsDone = g => (g||[]).filter(x=>x.status==='achieved').length;

  return (
    <>
      <style>{css}</style>
      <div className="pi">

        {view==='list'&&(
          <>
            <div className="pi-title">Individual Learning Plans</div>
            <div className="pi-sub">View your child's learning plans, goals, and progress.</div>

            {!activeChild ? <div className="empty">Select a child to view their ILPs.</div>
            : loading ? <div style={{textAlign:'center',padding:'48px',color:'#9094a8',fontSize:'.82rem'}}><span className="spin"/> Loading…</div>
            : !ilps.length ? <div className="empty">No active learning plans for {activeChild.user?.name||'this child'}.</div>
            : ilps.map(i => {
                const sc=STATUS_COLORS[i.status]||{bg:'#f0f2f7',color:'#6b7194'};
                const done=goalsDone(i.goals); const total=i.goals?.length||0; const pct=total?Math.round(done/total*100):0;
                return (
                  <div key={i._id} className="card" onClick={()=>openDetail(i)}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:8,gap:8}}>
                      <div>
                        <div style={{fontWeight:700,fontSize:'.88rem',color:'#0f1123'}}>{i.academicYear} Learning Plan</div>
                        <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:2,textTransform:'capitalize'}}>{(i.category||'').replace('_',' ')} · {i.reviewFrequency} reviews</div>
                        <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:1}}>Teacher: {i.createdBy?.name||'—'}</div>
                      </div>
                      <span className="pill" style={{background:sc.bg,color:sc.color,flexShrink:0}}>{i.status?.replace('_',' ')}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',fontSize:'.72rem',color:'#9094a8',marginBottom:4}}>
                      <span>Goals {done}/{total} · {i.progressUpdates?.length||0} updates</span>
                      {i.parentAcknowledged
                        ? <span style={{color:'#10c98e',fontWeight:600}}>✓ Acknowledged</span>
                        : <span style={{color:'#f97316',fontWeight:600}}>Acknowledgement needed</span>
                      }
                    </div>
                    {total>0&&<div className="progress-bar-wrap"><div className="progress-bar" style={{width:pct+'%'}}/></div>}
                  </div>
                );
              })
            }
          </>
        )}

        {view==='detail'&&selected&&(
          <>
            <button className="btn btn-ghost btn-sm" style={{marginBottom:16}} onClick={()=>{setView('list');setSelected(null);}}><ChevronLeftIcon/>Back to ILPs</button>

            <div className="detail">
              <div style={{padding:'18px 22px',borderBottom:'1px solid #f0f2f8',display:'flex',alignItems:'flex-start',justifyContent:'space-between',flexWrap:'wrap',gap:12}}>
                <div>
                  <div style={{fontWeight:700,fontSize:'1rem',color:'#0f1123'}}>{selected.academicYear} Individual Learning Plan</div>
                  <div style={{fontSize:'.75rem',color:'#9094a8',marginTop:3}}>Teacher: {selected.createdBy?.name} · <span style={{textTransform:'capitalize'}}>{(selected.category||'').replace('_',' ')}</span></div>
                  <div style={{display:'flex',gap:8,marginTop:8}}>
                    <span className="pill" style={{...(STATUS_COLORS[selected.status]||{bg:'#f0f2f7',color:'#6b7194'})}}>{selected.status?.replace('_',' ')}</span>
                  </div>
                </div>
                <div style={{fontSize:'.72rem',color:'#9094a8',textAlign:'right'}}>
                  <div>Next review: {fmtD(selected.nextReviewDate)}</div>
                  <div style={{marginTop:3}}>{selected.progressUpdates?.length||0} progress updates</div>
                </div>
              </div>

              {/* Acknowledge banner */}
              {selected.parentAcknowledged
                ? <div className="ack-banner" style={{margin:'14px 18px 0'}}><CheckCircleIcon/><div><strong>You acknowledged this ILP</strong> on {fmtD(selected.parentAcknowledgedAt)}</div></div>
                : <div className="ack-btn-wrap" style={{margin:'14px 18px 0'}}>
                    <div style={{fontSize:'.8rem',fontWeight:600,color:'#92400e',marginBottom:8}}>Please acknowledge you have reviewed this learning plan.</div>
                    <button className="btn btn-orange btn-sm" onClick={acknowledge}><CheckCircleIcon/>Acknowledge ILP</button>
                  </div>
              }

              <div className="detail-tabs" style={{marginTop:14}}>
                {['overview','goals','progress','comments'].map(t=>(
                  <button key={t} className={`dtab${detailTab===t?' on':''}`} onClick={()=>setDetailTab(t)} style={{textTransform:'capitalize'}}>{t}{t==='comments'&&selected.parentComments?.length?` (${selected.parentComments.length})`:''}</button>
                ))}
              </div>

              <div className="detail-body">

                {detailTab==='overview'&&(
                  <div>
                    <div className="section-title">About This Plan</div>
                    <div className="info-grid">
                      <div className="info-item"><div className="info-lbl">Start Date</div><div className="info-val">{fmtD(selected.startDate)}</div></div>
                      <div className="info-item"><div className="info-lbl">End Date</div><div className="info-val">{fmtD(selected.endDate)}</div></div>
                      <div className="info-item"><div className="info-lbl">Review Schedule</div><div className="info-val" style={{textTransform:'capitalize'}}>{selected.reviewFrequency}</div></div>
                      <div className="info-item"><div className="info-lbl">Next Review</div><div className="info-val">{fmtD(selected.nextReviewDate)}</div></div>
                    </div>

                    {selected.currentLevel?.strengths&&(
                      <><div className="section-title">Strengths</div>
                      <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#ecfdf5',borderRadius:9,padding:'10px 12px',border:'1px solid #a7f3d0',marginBottom:12}}>{selected.currentLevel.strengths}</div></>
                    )}
                    {selected.currentLevel?.weaknesses&&(
                      <><div className="section-title">Areas for Improvement</div>
                      <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#fff7ed',borderRadius:9,padding:'10px 12px',border:'1px solid #fed7aa',marginBottom:12}}>{selected.currentLevel.weaknesses}</div></>
                    )}
                    {selected.currentLevel?.learningGaps&&(
                      <><div className="section-title">Learning Gaps</div>
                      <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4',marginBottom:12}}>{selected.currentLevel.learningGaps}</div></>
                    )}
                    {selected.accommodations&&(
                      <><div className="section-title">Accommodations</div>
                      <div style={{fontSize:'.82rem',color:'#3a3f58',lineHeight:1.6,background:'#f8f9fc',borderRadius:9,padding:'10px 12px',border:'1px solid #e8ebf4'}}>{selected.accommodations}</div></>
                    )}
                  </div>
                )}

                {detailTab==='goals'&&(
                  <div>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                      <div className="section-title" style={{margin:0}}>Goals</div>
                      <div style={{fontSize:'.72rem',color:'#9094a8'}}>{goalsDone(selected.goals)}/{selected.goals?.length||0} achieved</div>
                    </div>
                    <div className="progress-bar-wrap" style={{marginBottom:16}}><div className="progress-bar" style={{width:(selected.goals?.length?Math.round(goalsDone(selected.goals)/selected.goals.length*100):0)+'%'}}/></div>
                    {(selected.goals||[]).map((g,idx)=>{
                      const gs=GOAL_STATUS_COLORS[g.status]||GOAL_STATUS_COLORS.not_started;
                      return <div key={idx} className="goal-card">
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8,marginBottom:6}}>
                          <div style={{fontSize:'.67rem',fontWeight:700,color:'#9094a8',textTransform:'uppercase'}}>{g.area||'General'} · <span style={{textTransform:'capitalize'}}>{g.type?.replace('_',' ')}</span></div>
                          <span className="pill" style={{...gs,textTransform:'capitalize',flexShrink:0}}>{g.status?.replace('_',' ')}</span>
                        </div>
                        <div style={{fontWeight:600,fontSize:'.83rem',color:'#1a1d2e',marginBottom:g.strategy?6:0}}>{g.description}</div>
                        {g.successCriteria&&<div style={{fontSize:'.76rem',color:'#6b7194',marginTop:4}}><strong>Success when:</strong> {g.successCriteria}</div>}
                        {g.targetDate&&<div style={{fontSize:'.72rem',color:'#9094a8',marginTop:4}}>Target date: {fmtD(g.targetDate)}</div>}
                      </div>;
                    })}
                  </div>
                )}

                {detailTab==='progress'&&(
                  <div>
                    <div className="section-title">Progress Timeline</div>
                    {!(selected.progressUpdates?.length)&&<div style={{color:'#b0b5cc',fontSize:'.82rem'}}>No progress updates yet.</div>}
                    {[...(selected.progressUpdates||[])].reverse().map((u,idx)=>{
                      const rc=RATING_COLORS[u.overallRating];
                      return <div key={idx} className="update-card">
                        <div style={{fontSize:'.7rem',color:'#9094a8',marginBottom:6}}>
                          <strong>{u.updatedBy?.name||'Teacher'}</strong> · {fmtDT(u.date||u.createdAt)}
                          {rc&&<span className="pill" style={{...rc,marginLeft:8,textTransform:'capitalize'}}>{u.overallRating?.replace('_',' ')}</span>}
                        </div>
                        <div style={{fontSize:'.82rem',color:'#1a1d2e',lineHeight:1.55}}>{u.note}</div>
                      </div>;
                    })}
                  </div>
                )}

                {detailTab==='comments'&&(
                  <div>
                    <div className="section-title">Your Comments & Teacher Replies</div>
                    {!(selected.parentComments?.length)&&<div style={{color:'#b0b5cc',fontSize:'.82rem',marginBottom:16}}>No comments yet. Add one below.</div>}
                    {(selected.parentComments||[]).map((cm,idx)=>(
                      <div key={idx} className="my-comment">
                        <div style={{fontSize:'.7rem',color:'#c2410c',marginBottom:5}}>You · {fmtDT(cm.date||cm.createdAt)}</div>
                        <div style={{fontSize:'.82rem',color:'#1a1d2e'}}>{cm.comment}</div>
                        {cm.reply
                          ? <div className="reply-box"><strong>Teacher ({cm.repliedBy?.name}):</strong> {cm.reply}<span style={{color:'#d97706',fontWeight:400,marginLeft:6}}>· {fmtD(cm.repliedAt)}</span></div>
                          : <div style={{fontSize:'.72rem',color:'#9094a8',marginTop:6,fontStyle:'italic'}}>Awaiting teacher reply…</div>
                        }
                      </div>
                    ))}

                    <div className="comment-input-wrap">
                      <div style={{fontSize:'.76rem',fontWeight:600,color:'#6b7194',marginBottom:8,display:'flex',alignItems:'center',gap:6}}><ChatBubbleLeftRightIcon style={{width:14,height:14}}/>Add a comment for the teacher</div>
                      <textarea className="comment-input" value={comment} onChange={e=>setComment(e.target.value)} placeholder="Share your observations or questions about this learning plan…"/>
                      <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
                        <button className="btn btn-orange btn-sm" onClick={submitComment} disabled={sending||!comment.trim()}>{sending?'Sending…':'Send Comment'}</button>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}